import { z } from "zod";

import { isValidBrazilDocument } from "@/shared/utils/brazilDocument";

/**
 * Who pays a PIX or SPEI collection. Messages are injected so the contracts are testable without an i18n
 * runtime, as in `psePayer.schema.ts`.
 */

export interface BrazilPayerMessages {
  name: string;
  /** Neither 11 nor 14 digits. */
  document: string;
  /** The right length, but the check digits do not add up. */
  documentCheck: string;
}

/** A PIX QR is minted for a named payer with a CPF or a CNPJ: the provider refuses one without them. */
export function brazilPayerSchema(messages: BrazilPayerMessages) {
  return z.object({
    payerName: z.string().trim().min(2, messages.name).max(120, messages.name),
    document: z.string().superRefine((value, ctx) => {
      const length = value.replace(/\D/g, "").length;
      if (length !== 11 && length !== 14) {
        ctx.addIssue({ code: "custom", message: messages.document });
      } else if (!isValidBrazilDocument(value)) {
        ctx.addIssue({ code: "custom", message: messages.documentCheck });
      }
    }),
  });
}

export type BrazilPayerValues = z.input<ReturnType<typeof brazilPayerSchema>>;

export interface MexicoPayerMessages {
  name: string;
  rfc: string;
}

/**
 * SPEI only demands the payer's name. The RFC is accepted as typed (up to the 13 characters the backend
 * stores) and never checked against a pattern: refusing a valid-but-unusual one would block a real
 * collection.
 */
export function mexicoPayerSchema(messages: MexicoPayerMessages) {
  return z.object({
    payerName: z.string().trim().min(2, messages.name).max(120, messages.name),
    rfc: z.string().trim().max(13, messages.rfc),
  });
}

export type MexicoPayerValues = z.input<ReturnType<typeof mexicoPayerSchema>>;
